import React, { useEffect, useRef, useState } from 'react';
import { AUTOBASE_CONTINUE_URL, AUTOBASE_LOG_STATE_URL, AUTOBASE_PREVIEW_URL, GET_PRODUCT_BY_ID, deleteFetchRequest, getFetchRequest, postFetchRequest } from '../store/requests';
import { makeUrl, errorCallback } from '../constant';

const AutobaseRunPanel = ({ initialPartNumber = '', autoStart = false, onAddToOrder }) => {
    const [partNumber, setPartNumber] = useState(initialPartNumber || '');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [logState, setLogState] = useState(null);
    const [isResolvingChoice, setIsResolvingChoice] = useState(false);
    const [choiceError, setChoiceError] = useState(null);
    const [manualManufacturer, setManualManufacturer] = useState('');
    const [manualDescription, setManualDescription] = useState('');
    const [manualCategory, setManualCategory] = useState('');
    const [manualImageUrl, setManualImageUrl] = useState('');
    const [manualDatasheetUrl, setManualDatasheetUrl] = useState('');
    const [manualAttributes, setManualAttributes] = useState([{ key: '', value: '' }]);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [deleteSuccess, setDeleteSuccess] = useState(null);

    const pollTimerRef = useRef(null);
    const pollGenRef = useRef(0);
    const pollStartedAtRef = useRef(0);
    const continuingRef = useRef(null);
    const autoStartedRef = useRef(false);
    const [isContinuing, setIsContinuing] = useState(false);

    const stopLogPoll = () => {
        pollGenRef.current += 1;
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    };

    const isLogTerminal = (state) => {
        if (!state) {
            return false;
        }
        const dbStatus = ((state.db || {}).status || '');
        const ignoreWait = continuingRef.current;
        if (((state.mouser || {}).status || '') === 'awaiting_choice') {
            return ignoreWait !== 'mouser';
        }
        if (((state.digikey || {}).status || '') === 'awaiting_choice') {
            return ignoreWait !== 'digikey';
        }
        if (((state.lcsc || {}).status || '') === 'awaiting_choice') {
            return ignoreWait !== 'lcsc';
        }
        if (dbStatus === 'awaiting_choice') {
            return ignoreWait !== 'db';
        }
        if (dbStatus === 'awaiting_manual') {
            return ignoreWait !== 'manual';
        }
        if (dbStatus === 'skipped' && !continuingRef.current) {
            return true;
        }
        const stepKeys = ['mouser', 'digikey', 'lcsc', 'db', 'media', 'solr'];
        const terminal = ['ok', 'not_found', 'error', 'skipped', 'partial'];
        return stepKeys.every((key) => terminal.includes((state[key] || {}).status));
    };

    const applyLogState = (state) => {
        setLogState(state);
        if (state && state.result) {
            setResult(state.result);
        }
        if (isLogTerminal(state)) {
            setIsLoading(false);
            setIsContinuing(false);
            continuingRef.current = null;
            return true;
        }
        return false;
    };

    const startLogPoll = (runId, expectedPartNumber) => {
        const gen = pollGenRef.current + 1;
        pollGenRef.current = gen;
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        pollStartedAtRef.current = Date.now();

        const tick = () => {
            if (pollGenRef.current !== gen) {
                return;
            }
            if (Date.now() - pollStartedAtRef.current > 240000) {
                setIsLoading(false);
                setError('Прогон слишком долго не отвечает. Можно запустить ещё раз.');
                return;
            }
            getFetchRequest(
                makeUrl([AUTOBASE_LOG_STATE_URL]),
                (data) => {
                    if (pollGenRef.current !== gen) {
                        return;
                    }
                    const state = data || {};
                    if (state.runId !== runId || (expectedPartNumber && state.partNumber !== expectedPartNumber)) {
                        pollTimerRef.current = setTimeout(tick, 2000);
                        return;
                    }
                    if (!applyLogState(state)) {
                        pollTimerRef.current = setTimeout(tick, 2000);
                    }
                },
                (err) => {
                    if (pollGenRef.current !== gen) {
                        return;
                    }
                    errorCallback(err);
                    pollTimerRef.current = setTimeout(tick, 2000);
                },
            );
        };
        tick();
    };

    const parseRequestError = (err, fallback) => {
        let message = (err && err.message) || fallback;
        try {
            const parsed = JSON.parse(message);
            message = parsed.error || parsed.detail || message;
        } catch (parseError) {
            // текст ошибки уже человеческий
        }
        return message;
    };

    const handleSearch = (partNumberOverride) => {
        const trimmed = String(partNumberOverride != null ? partNumberOverride : (partNumber || '')).trim();
        if (!trimmed) {
            setError('Введите partNumber');
            return;
        }

        stopLogPoll();
        continuingRef.current = null;
        setIsContinuing(false);
        setIsLoading(true);
        setError(null);
        setResult(null);
        setLogState(null);
        setChoiceError(null);
        setManualManufacturer('');
        setManualDescription('');
        setManualCategory('');
        setManualImageUrl('');
        setManualDatasheetUrl('');
        setManualAttributes([{ key: '', value: '' }]);
        setDeleteError(null);
        setDeleteSuccess(null);

        postFetchRequest(
            makeUrl([AUTOBASE_PREVIEW_URL]),
            { partNumber: trimmed },
            (data) => {
                const runId = data && data.runId;
                if (!runId) {
                    setIsLoading(false);
                    setError('Не удалось запустить автозагрузку');
                    return;
                }
                startLogPoll(runId, trimmed);
            },
            (err) => {
                errorCallback(err);
                setIsLoading(false);
                setError(parseRequestError(
                    err,
                    'Кто-то из администраторов в данный момент занимается этим партномером, пожалуйста ожидайте.',
                ));
            },
        );
    };

    useEffect(() => {
        const trimmed = (initialPartNumber || '').trim();
        if (trimmed) {
            setPartNumber(trimmed);
        }
        if (autoStart && trimmed && !autoStartedRef.current) {
            autoStartedRef.current = true;
            handleSearch(trimmed);
        }
        return () => {
            stopLogPoll();
        };
        // автозапуск только при монтировании панели
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const describeStep = (kind, step) => {
        const status = (step && step.status) || 'idle';
        const raw = String((step && step.message) || '').trim();
        const badges = {
            idle: 'Ожидание',
            pending: 'В работе',
            ok: 'Готово',
            not_found: 'Не нашли',
            skipped: 'Пропущено',
            error: 'Ошибка',
            awaiting_choice: 'Нужен выбор',
            awaiting_manual: 'Нужен ручной ввод',
            partial: 'Частично',
        };
        const tones = {
            idle: 'idle',
            pending: 'pending',
            ok: 'ok',
            not_found: 'warn',
            skipped: 'idle',
            error: 'error',
            awaiting_choice: 'warn',
            awaiting_manual: 'warn',
            partial: 'warn',
        };

        const withoutIds = raw
            .replace(/\s*id=[0-9a-f-]{8,}/gi, '')
            .replace(/\s*\([0-9a-f-]{8,}\)/gi, '')
            .replace(/\s+/g, ' ')
            .trim();

        let hint = '';
        if (kind === 'mouser') {
            if (status === 'idle') hint = 'Пока не начали искать этот партномер в каталоге Mouser.';
            else if (status === 'pending') hint = 'Ищем точное совпадение партномера в Mouser.';
            else if (status === 'ok') hint = 'Нашли товар в Mouser.';
            else if (status === 'not_found') hint = 'В Mouser нет точного совпадения. Смотрим другие источники.';
            else if (status === 'awaiting_choice') hint = 'В Mouser несколько похожих карточек. Выберите нужную ниже.';
            else if (status === 'error') hint = 'Не удалось запросить Mouser. Прогон пойдёт дальше по другим источникам.';
            else if (status === 'skipped') hint = 'Поиск в Mouser не выполнялся.';
        } else if (kind === 'digikey') {
            if (status === 'idle') hint = 'Пока не начали искать этот партномер в DigiKey.';
            else if (status === 'pending') hint = 'Ищем товар в DigiKey и дополняем карточку.';
            else if (status === 'ok') hint = 'Нашли данные в DigiKey и дополнили карточку.';
            else if (status === 'not_found') hint = 'В DigiKey этот партномер не нашли.';
            else if (status === 'awaiting_choice') hint = 'В DigiKey несколько вариантов. Выберите нужный ниже.';
            else if (status === 'error') hint = 'Не удалось запросить DigiKey. Прогон пойдёт дальше.';
            else if (status === 'skipped') hint = 'Поиск в DigiKey не выполнялся.';
        } else if (kind === 'lcsc') {
            if (status === 'idle') hint = 'Пока не обращались к LCSC.';
            else if (status === 'pending') hint = 'Ищем товар или даташит на LCSC.';
            else if (status === 'ok') hint = 'Данные с LCSC добавлены в карточку.';
            else if (status === 'not_found') hint = 'На LCSC не нашли товар или ссылку на даташит.';
            else if (status === 'awaiting_choice') hint = 'На LCSC несколько вариантов. Выберите нужный ниже.';
            else if (status === 'error') hint = 'Не удалось запросить LCSC.';
            else if (status === 'skipped') hint = 'LCSC не понадобился: товар и даташит уже есть из Mouser и DigiKey.';
        } else if (kind === 'db') {
            if (status === 'idle') hint = 'Карточку ещё не сохраняли.';
            else if (status === 'pending') hint = 'Проверяем каталог и сохраняем карточку.';
            else if (status === 'ok') hint = 'Карточка есть в каталоге.';
            else if (status === 'awaiting_choice') hint = 'В каталоге уже есть похожие карточки. Выберите, какую использовать, или создайте новую.';
            else if (status === 'awaiting_manual') hint = 'Источники ничего не нашли. Заполните карточку вручную в форме ниже.';
            else if (status === 'error') hint = 'Не получилось сохранить карточку.';
            else if (status === 'skipped') hint = 'Сохранение не выполнялось.';
        } else if (kind === 'photo') {
            if (status === 'idle') hint = 'Фото ещё не загружали.';
            else if (status === 'pending') hint = 'Скачиваем фото товара.';
            else if (status === 'ok') hint = 'Фото сохранено.';
            else if (status === 'skipped') hint = 'Ссылки на фото не было — этот пункт пропускаем.';
            else if (status === 'error') hint = 'Фото не удалось скачать. Карточка всё равно сохранится.';
        } else if (kind === 'datasheet') {
            if (status === 'idle') hint = 'Даташит ещё не загружали.';
            else if (status === 'pending') hint = 'Скачиваем PDF с документацией.';
            else if (status === 'ok') hint = 'Даташит сохранён.';
            else if (status === 'skipped') hint = 'Ссылки на даташит не было — этот пункт пропускаем.';
            else if (status === 'error') hint = 'Даташит не удалось скачать. Карточка всё равно сохранится.';
        } else if (kind === 'solr') {
            if (status === 'idle') hint = 'Поиск на сайте ещё не обновляли.';
            else if (status === 'pending') hint = 'Отправляем карточку в поиск на сайте.';
            else if (status === 'ok') hint = 'Карточка ушла в поиск на сайте. Через короткое время товар можно найти по партномеру.';
            else if (status === 'error') hint = 'Не удалось обновить поиск на сайте. Карточка в каталоге уже есть.';
            else if (status === 'skipped') hint = 'Обновление поиска не выполнялось.';
        }

        const known = [
            [/Товар найден в Mouser/i, 'Нашли товар в Mouser.'],
            [/Mouser-товар обогащён данными DigiKey/i, 'Нашли данные в DigiKey и дополнили карточку.'],
            [/Товар найден в DigiKey/i, 'Нашли товар в DigiKey.'],
            [/Товар найден на LCSC/i, 'Нашли товар на LCSC и дополнили карточку.'],
            [/Точное совпадение по partNumber в Mouser не найдено/i, 'В Mouser нет точного совпадения. Смотрим другие источники.'],
            [/Товар не найден в DigiKey при обогащении/i, 'В DigiKey этот партномер не нашли, карточку оставляем как есть.'],
            [/Товар не найден в DigiKey \(fallback\)/i, 'В DigiKey этот партномер не нашли.'],
            [/Товар не найден на LCSC/i, 'На LCSC этот партномер не нашли.'],
            [/empty partNumber/i, 'Партномер пустой, поиск не запускался.'],
            [/Товар уже существует/i, 'Такой товар уже был в каталоге. Новую карточку не создавали, при необходимости дополнили данные.'],
            [/Карточка сохранена/i, 'Новая карточка успешно сохранена в каталог.'],
            [/LCSC не требуется/i, 'LCSC не понадобился: товар уже нашли в Mouser и DigiKey, ссылка на даташит тоже есть.'],
            [/Даташит взят с LCSC/i, 'Mouser и DigiKey не дали ссылку на даташит, взяли её с LCSC.'],
            [/Mouser и DigiKey без даташита/i, 'Mouser и DigiKey ссылку на даташит не дали, на LCSC её тоже нет.'],
            [/Нет ссылок на фото/i, 'Ссылки на фото не было — этот пункт пропускаем.'],
            [/Нет ссылки на даташит/i, 'Ссылки на даташит не было — этот пункт пропускаем.'],
            [/Пропущено из-за ошибки предыдущего шага/i, 'Этот шаг не выполняли, потому что предыдущий завершился с ошибкой.'],
            [/Прогон отменён/i, 'Прогон отменён, партномер снова свободен.'],
        ];
        const matched = known.find(([pattern]) => pattern.test(withoutIds));
        if (matched) {
            hint = matched[1];
        } else if (withoutIds && status !== 'idle' && status !== 'pending') {
            const technical = /exception|traceback|status code|errno|proxy|timeout|ssl/i.test(withoutIds);
            if (!technical) {
                hint = withoutIds;
            } else if (hint) {
                hint = `${hint} Техническая деталь: ${withoutIds}`;
            } else {
                hint = `Что-то пошло не так. Техническая деталь: ${withoutIds}`;
            }
        }

        const extras = [];
        if (kind === 'mouser' && step && step.name) extras.push(`Название: ${step.name}`);
        if (kind === 'mouser' && step && step.datasheetUrl) extras.push('Ссылка на даташит в Mouser есть.');
        if (kind === 'mouser' && step && !step.datasheetUrl && status === 'ok') extras.push('Ссылки на даташит в Mouser нет.');
        if (kind === 'digikey' && typeof step.attributesAdded === 'number' && step.attributesAdded > 0) {
            extras.push(`Добавлено характеристик: ${step.attributesAdded}.`);
        }
        if (kind === 'lcsc' && step && step.datasheetUrl) extras.push('Ссылка на даташит с LCSC получена.');
        if ((kind === 'photo' || kind === 'datasheet') && status === 'ok' && withoutIds && hint.indexOf(withoutIds) === -1) {
            extras.push(withoutIds);
        }

        return {
            badge: badges[status] || status,
            tone: tones[status] || 'idle',
            hint: [hint, ...extras].filter(Boolean).join(' '),
        };
    };

    const renderStepStatus = (title, step, kind, nested) => {
        const described = describeStep(kind, step);
        return (
            <div className={`autobase-step${nested ? ' autobase-step--nested' : ''}`}>
                <div className="autobase-step__title">{title}</div>
                <div className={`autobase-step__badge autobase-step__badge--${described.tone}`}>
                    {described.badge}
                </div>
                <div className="autobase-step__hint">{described.hint}</div>
            </div>
        );
    };

    const terminalStatuses = ['ok', 'not_found', 'error', 'skipped', 'partial'];
    const isSolrStepDone =
        !!(logState && terminalStatuses.includes(((logState.solr || {}).status || '').toLowerCase()));
    const productId = (result && (result.productId || result.product_id))
        || ((logState && logState.db) || {}).productId;
    const sourceChoiceMeta = {
        mouser: { title: 'В Mouser несколько точных совпадений. Выберите, какой товар качать', openLabel: 'Открыть на Mouser' },
        digikey: { title: 'В DigiKey несколько вариантов. Выберите, какой использовать', openLabel: 'Открыть на DigiKey' },
        lcsc: { title: 'На LCSC несколько вариантов. Выберите, какой использовать', openLabel: 'Открыть на LCSC' },
    };
    const sourceChoiceKey = (!isContinuing && logState && (
        (((logState.mouser || {}).status === 'awaiting_choice') && 'mouser')
        || (((logState.digikey || {}).status === 'awaiting_choice') && 'digikey')
        || (((logState.lcsc || {}).status === 'awaiting_choice') && 'lcsc')
        || null
    )) || null;
    const sourceChoiceStep = sourceChoiceKey ? (logState[sourceChoiceKey] || {}) : {};
    const isAwaitingChoice = !isContinuing && ((logState && logState.db) || {}).status === 'awaiting_choice';
    const isAwaitingManual = !isContinuing
        && ((logState && logState.db) || {}).status === 'awaiting_manual';
    const stepStarted = (step) => {
        const status = (step && step.status) || 'idle';
        return Boolean(status && status !== 'idle');
    };
    const choiceCandidates = (logState && Array.isArray(logState.choiceCandidates))
        ? logState.choiceCandidates
        : [];
    const foundProduct = (logState && logState.foundProduct) || (result && result.product) || {};
    const foundPartNumber = foundProduct.partNumber || foundProduct.part_number || (partNumber || '').trim();
    const foundManufacturer = foundProduct.manufacturer || '';
    const foundCategory = foundProduct.category || '';
    const foundDescription = foundProduct.description || '';
    const foundSource = foundProduct.sourceUsed || foundProduct.source_used || (result && result.source_used) || '';
    const currentRunId = logState && logState.runId;

    const handleOpenCandidateCard = (candidateId) => {
        if (!candidateId) {
            return;
        }
        window.open(`https://auroraelec.ru/product/${candidateId}`, '_blank', 'noopener');
    };

    const handleOpenSourceCandidate = (url) => {
        if (!url) {
            return;
        }
        window.open(url, '_blank', 'noopener');
    };

    const resolveChoice = (body) => {
        if (!currentRunId || isResolvingChoice) {
            return;
        }
        setIsResolvingChoice(true);
        setChoiceError(null);
        setIsLoading(true);
        postFetchRequest(
            makeUrl([AUTOBASE_CONTINUE_URL]),
            { runId: currentRunId, ...body },
            () => {
                setIsResolvingChoice(false);
                const sourceId = (body && body.sourceCandidateId) || '';
                const waitKind = sourceId.startsWith('digikey-')
                    ? 'digikey'
                    : (sourceId.startsWith('lcsc-')
                        ? 'lcsc'
                        : (sourceId ? 'mouser' : (body && body.manual ? 'manual' : 'db')));
                continuingRef.current = waitKind;
                setIsContinuing(true);
                setLogState((prev) => {
                    if (!prev) {
                        return prev;
                    }
                    if (sourceId) {
                        const sourceKey = waitKind;
                        return {
                            ...prev,
                            [sourceKey]: {
                                ...(prev[sourceKey] || {}),
                                status: 'pending',
                                message: 'Продолжение по выбранному варианту...',
                            },
                            choiceCandidates: [],
                            choiceKind: null,
                        };
                    }
                    return {
                        ...prev,
                        db: {
                            ...(prev.db || {}),
                            status: 'pending',
                            message: 'Сохранение карточки...',
                        },
                    };
                });
                startLogPoll(currentRunId, (partNumber || '').trim());
            },
            (err) => {
                errorCallback(err);
                continuingRef.current = null;
                setIsContinuing(false);
                setIsResolvingChoice(false);
                setIsLoading(false);
                setChoiceError(parseRequestError(err, 'Не удалось продолжить автозагрузку'));
            },
        );
    };

    const submitManualCard = () => {
        if (!currentRunId || isResolvingChoice) {
            return;
        }
        const manufacturer = (manualManufacturer || '').trim();
        const description = (manualDescription || '').trim();
        if (!manufacturer || !description) {
            setChoiceError('Укажите производителя и описание');
            return;
        }
        const attributes = (manualAttributes || [])
            .map((item) => ({
                attributeKey: (item.key || '').trim(),
                attributeValue: (item.value || '').trim(),
            }))
            .filter((item) => item.attributeKey && item.attributeValue);
        resolveChoice({
            manual: true,
            manufacturer,
            description,
            category: (manualCategory || '').trim(),
            imageUrl: (manualImageUrl || '').trim(),
            datasheetUrl: (manualDatasheetUrl || '').trim(),
            attributes,
        });
    };

    const resetManualForm = () => {
        setManualManufacturer('');
        setManualDescription('');
        setManualCategory('');
        setManualImageUrl('');
        setManualDatasheetUrl('');
        setManualAttributes([{ key: '', value: '' }]);
        setChoiceError(null);
    };

    const cancelRun = () => {
        if (!currentRunId || isResolvingChoice) {
            return;
        }
        setIsResolvingChoice(true);
        setChoiceError(null);
        postFetchRequest(
            makeUrl([AUTOBASE_CONTINUE_URL]),
            { runId: currentRunId, cancel: true },
            () => {
                stopLogPoll();
                continuingRef.current = null;
                setIsContinuing(false);
                setIsResolvingChoice(false);
                setIsLoading(false);
                setLogState(null);
                setResult(null);
                resetManualForm();
                setDeleteSuccess('Прогон отменён, партномер разблокирован');
            },
            (err) => {
                errorCallback(err);
                setIsResolvingChoice(false);
                setChoiceError(parseRequestError(err, 'Не удалось отменить прогон'));
            },
        );
    };

    const updateManualAttribute = (index, field, value) => {
        setManualAttributes((prev) => {
            const next = prev.map((item, itemIndex) => (
                itemIndex === index ? { ...item, [field]: value } : item
            ));
            const last = next[next.length - 1];
            if (last && ((last.key || '').trim() || (last.value || '').trim())) {
                next.push({ key: '', value: '' });
            }
            return next;
        });
    };

    const removeManualAttribute = (index) => {
        setManualAttributes((prev) => {
            const next = prev.filter((_, itemIndex) => itemIndex !== index);
            if (!next.length || (next[next.length - 1].key || next[next.length - 1].value)) {
                next.push({ key: '', value: '' });
            }
            return next;
        });
    };

    const handleOpenProductCard = () => {
        if (!productId) {
            return;
        }
        const url = `https://auroraelec.ru/product/${productId}`;
        window.open(url, '_blank', 'noopener');
    };

    const handleDeleteProduct = () => {
        if (!productId || isDeleting) {
            return;
        }
        // Дополнительная защита от случайного удаления
        // eslint-disable-next-line no-alert
        const confirmed = window.confirm('Точно удалить товар и его индексацию? Это действие необратимо.');
        if (!confirmed) {
            return;
        }

        setIsDeleting(true);
        setDeleteError(null);
        setDeleteSuccess(null);

        const productUrl = makeUrl([GET_PRODUCT_BY_ID, `${productId}/`]);
        const markDeleted = () => {
            setIsDeleting(false);
            setDeleteError(null);
            setDeleteSuccess('Товар удалён');
            setResult(null);
            setLogState(null);
            setPartNumber('');
        };

        deleteFetchRequest(
            productUrl,
            markDeleted,
            (err) => {
                errorCallback(err);
                const raw = (err && err.message) || '';
                const isNetworkFail = /failed to fetch/i.test(raw);
                // DELETE уже мог пройти (БД закоммичена), а ответ оборвался — проверяем GET
                if (isNetworkFail) {
                    getFetchRequest(
                        productUrl,
                        () => {
                            setIsDeleting(false);
                            setDeleteError(raw || 'Не удалось удалить товар');
                        },
                        () => {
                            markDeleted();
                        },
                    );
                    return;
                }
                setIsDeleting(false);
                let message = raw || 'Не удалось удалить товар';
                try {
                    const parsed = JSON.parse(message);
                    message = parsed.error || parsed.detail || message;
                } catch (parseError) {
                    // текст ошибки уже человеческий
                }
                setDeleteError(message);
            },
        );
    };

    return (
        <div className="autobase-run-panel">
            {error && (
                <div className="logs-panel__error" style={{ marginBottom: '10px' }}>
                    {error}
                </div>
            )}
            {deleteSuccess && (
                <div className="logs-panel__success" style={{ marginBottom: '10px', color: '#3c763d' }}>
                    {deleteSuccess}
                </div>
            )}

            {logState && (
                <div className="logs-panel__body" style={{ marginBottom: '10px' }}>
                    <div className="autobase-steps">
                        {renderStepStatus('Шаг 1. Поиск в Mouser', logState.mouser, 'mouser')}
                        {renderStepStatus('Шаг 2. Поиск в DigiKey', logState.digikey, 'digikey')}
                        {renderStepStatus('Шаг 3. Поиск на LCSC', logState.lcsc, 'lcsc')}
                        {stepStarted(logState.db) && renderStepStatus('Шаг 4. Сохранение карточки', logState.db, 'db')}
                        {stepStarted(logState.media) && (
                            <div className="autobase-step">
                                <div className="autobase-step__title">Шаг 5. Фото и документация</div>
                                <div className="autobase-step__hint">
                                    Скачиваем картинку товара и PDF с характеристиками, если источники дали ссылки.
                                </div>
                                <div className="autobase-step__children">
                                    {renderStepStatus(
                                        'Фото',
                                        (logState.media && logState.media.photo) || logState.media,
                                        'photo',
                                        true,
                                    )}
                                    {renderStepStatus(
                                        'Даташит',
                                        (logState.media && logState.media.datasheet) || { status: 'idle' },
                                        'datasheet',
                                        true,
                                    )}
                                </div>
                            </div>
                        )}
                        {stepStarted(logState.solr) && renderStepStatus('Шаг 6. Поиск на сайте', logState.solr, 'solr')}
                    </div>
                    {sourceChoiceKey && (
                        <div className="autobase-choice" style={{ marginTop: '12px' }}>
                            <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                                {sourceChoiceMeta[sourceChoiceKey].title}
                            </div>
                            {sourceChoiceStep.message && (
                                <div style={{ marginBottom: '8px' }}>{sourceChoiceStep.message}</div>
                            )}
                            {choiceError && (
                                <div className="logs-panel__error" style={{ marginBottom: '6px' }}>
                                    {choiceError}
                                </div>
                            )}
                            {choiceCandidates.map((candidate) => {
                                const candidateId = candidate.sourceCandidateId;
                                const sourceUrl = candidate.externalUrl || candidate.external_url;
                                return (
                                    <div
                                        key={candidateId}
                                        className="autobase-choice__item"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            marginBottom: '8px',
                                            padding: '8px',
                                            border: '1px solid #ddd',
                                        }}
                                    >
                                        <div>
                                            <div>
                                                {(candidate.partNumber || partNumber || '').trim()}
                                                {' / '}
                                                {candidate.manufacturer || 'без производителя'}
                                            </div>
                                            <div style={{ color: '#555', fontSize: '12px' }}>
                                                Категория: {candidate.category || 'не указана'}
                                                {candidate.packaging ? ` · Packaging: ${candidate.packaging}` : ''}
                                            </div>
                                            {candidate.description && (
                                                <div style={{ color: '#666', fontSize: '12px' }}>
                                                    {candidate.description}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                className="logs-panel__reload"
                                                onClick={() => handleOpenSourceCandidate(sourceUrl)}
                                                disabled={isResolvingChoice || !sourceUrl}
                                                style={{ marginRight: '8px' }}
                                            >
                                                {sourceChoiceMeta[sourceChoiceKey].openLabel}
                                            </button>
                                            <button
                                                type="button"
                                                className="logs-panel__reload"
                                                onClick={() => resolveChoice({ sourceCandidateId: candidateId })}
                                                disabled={isResolvingChoice}
                                            >
                                                Выбрать
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <button
                                type="button"
                                className="logs-panel__reload"
                                onClick={cancelRun}
                                disabled={isResolvingChoice}
                            >
                                Отмена
                            </button>
                        </div>
                    )}
                    {isAwaitingChoice && (
                        <div className="autobase-choice" style={{ marginTop: '12px' }}>
                            <div style={{ marginBottom: '6px', fontWeight: 'bold' }}>
                                Найдено несколько вариантов. Выберите карточку или создайте новую
                            </div>
                            {((logState.db || {}).message) && (
                                <div style={{ marginBottom: '8px' }}>{logState.db.message}</div>
                            )}
                            <div
                                style={{
                                    marginBottom: '12px',
                                    padding: '10px',
                                    border: '1px solid #8ab4d8',
                                    background: '#f4f8fc',
                                }}
                            >
                                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                                    Спарсили из источника{foundSource ? ` (${foundSource})` : ''}
                                </div>
                                <div>
                                    {foundPartNumber || '—'}
                                    {' / '}
                                    {foundManufacturer || 'без производителя'}
                                </div>
                                <div style={{ color: '#555', fontSize: '12px', marginTop: '2px' }}>
                                    Категория: {foundCategory || 'не указана'}
                                </div>
                                {foundDescription && (
                                    <div style={{ color: '#666', fontSize: '12px', marginTop: '2px' }}>
                                        {foundDescription}
                                    </div>
                                )}
                            </div>
                            {choiceError && (
                                <div className="logs-panel__error" style={{ marginBottom: '6px' }}>
                                    {choiceError}
                                </div>
                            )}
                            {choiceCandidates.map((candidate) => {
                                const candidateId = candidate.productId || candidate.product_id;
                                return (
                                    <div
                                        key={candidateId}
                                        className="autobase-choice__item"
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: '8px',
                                            marginBottom: '8px',
                                            padding: '8px',
                                            border: '1px solid #ddd',
                                        }}
                                    >
                                        <div>
                                            <div>
                                                {(candidate.partNumber || partNumber || '').trim()}
                                                {' / '}
                                                {candidate.manufacturer || 'без производителя'}
                                            </div>
                                            <div style={{ color: '#555', fontSize: '12px' }}>
                                                Категория: {candidate.category || 'не указана'}
                                            </div>
                                            {candidate.description && (
                                                <div style={{ color: '#666', fontSize: '12px' }}>
                                                    {candidate.description}
                                                </div>
                                            )}
                                        </div>
                                        <div>
                                            <button
                                                type="button"
                                                className="logs-panel__reload"
                                                onClick={() => handleOpenCandidateCard(candidateId)}
                                                disabled={isResolvingChoice}
                                                style={{ marginRight: '8px' }}
                                            >
                                                Открыть карточку
                                            </button>
                                            <button
                                                type="button"
                                                className="logs-panel__reload"
                                                onClick={() => resolveChoice({ productId: candidateId })}
                                                disabled={isResolvingChoice}
                                            >
                                                Выбрать
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                    type="button"
                                    className="logs-panel__reload"
                                    onClick={() => resolveChoice({ createNew: true })}
                                    disabled={isResolvingChoice}
                                >
                                    {isResolvingChoice ? 'Продолжение...' : 'Добавить новый'}
                                </button>
                                <button
                                    type="button"
                                    className="logs-panel__reload"
                                    onClick={cancelRun}
                                    disabled={isResolvingChoice}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    )}
                    {isAwaitingManual && (
                        <div className="autobase-manual">
                            <div className="autobase-manual__title">
                                Источники ничего не нашли. Заполните карточку вручную
                            </div>
                            {((logState.db || {}).message) && (
                                <div className="autobase-manual__hint">{logState.db.message}</div>
                            )}
                            {choiceError && (
                                <div className="logs-panel__error" style={{ marginBottom: '8px' }}>
                                    {choiceError}
                                </div>
                            )}
                            <div className="autobase-manual__grid">
                                <label className="autobase-manual__field">
                                    Партномер
                                    <input
                                        className="autobase-manual__input"
                                        value={(partNumber || '').trim()}
                                        disabled
                                    />
                                </label>
                                <label className="autobase-manual__field">
                                    Производитель *
                                    <input
                                        className="autobase-manual__input"
                                        placeholder="Например Texas Instruments"
                                        value={manualManufacturer}
                                        onChange={(e) => setManualManufacturer(e.target.value)}
                                        disabled={isResolvingChoice}
                                    />
                                </label>
                                <label className="autobase-manual__field autobase-manual__field--full">
                                    Описание *
                                    <textarea
                                        className="autobase-manual__input autobase-manual__textarea"
                                        rows={4}
                                        placeholder="Краткое описание товара"
                                        value={manualDescription}
                                        onChange={(e) => setManualDescription(e.target.value)}
                                        disabled={isResolvingChoice}
                                    />
                                </label>
                                <label className="autobase-manual__field">
                                    Категория
                                    <input
                                        className="autobase-manual__input"
                                        placeholder="Например Transistors"
                                        value={manualCategory}
                                        onChange={(e) => setManualCategory(e.target.value)}
                                        disabled={isResolvingChoice}
                                    />
                                </label>
                                <label className="autobase-manual__field">
                                    Ссылка на фото
                                    <input
                                        className="autobase-manual__input"
                                        placeholder="https://..."
                                        value={manualImageUrl}
                                        onChange={(e) => setManualImageUrl(e.target.value)}
                                        disabled={isResolvingChoice}
                                    />
                                </label>
                                <label className="autobase-manual__field autobase-manual__field--full">
                                    Ссылка на даташит
                                    <input
                                        className="autobase-manual__input"
                                        placeholder="https://..."
                                        value={manualDatasheetUrl}
                                        onChange={(e) => setManualDatasheetUrl(e.target.value)}
                                        disabled={isResolvingChoice}
                                    />
                                </label>
                            </div>
                            <div className="autobase-manual__attrs">
                                <div className="autobase-manual__attrs-title">Атрибуты</div>
                                <div className="autobase-manual__attr-head">
                                    <span>Название</span>
                                    <span>Значение</span>
                                    <span />
                                </div>
                                {manualAttributes.map((item, index) => (
                                    <div className="autobase-manual__attr-row" key={`manual-attr-${index}`}>
                                        <input
                                            className="autobase-manual__input"
                                            placeholder="Package / Case"
                                            value={item.key}
                                            onChange={(e) => updateManualAttribute(index, 'key', e.target.value)}
                                            disabled={isResolvingChoice}
                                        />
                                        <input
                                            className="autobase-manual__input"
                                            placeholder="SOT-23"
                                            value={item.value}
                                            onChange={(e) => updateManualAttribute(index, 'value', e.target.value)}
                                            disabled={isResolvingChoice}
                                        />
                                        <button
                                            type="button"
                                            className="autobase-manual__attr-remove"
                                            onClick={() => removeManualAttribute(index)}
                                            disabled={isResolvingChoice || manualAttributes.length === 1}
                                            title="Удалить атрибут"
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))}
                                <button
                                    type="button"
                                    className="logs-panel__reload"
                                    onClick={() => setManualAttributes((prev) => {
                                        const last = prev[prev.length - 1];
                                        if (last && !(last.key || '').trim() && !(last.value || '').trim()) {
                                            return prev;
                                        }
                                        return [...prev, { key: '', value: '' }];
                                    })}
                                    disabled={isResolvingChoice}
                                >
                                    Добавить атрибут
                                </button>
                            </div>
                            <div className="autobase-manual__actions">
                                <button
                                    type="button"
                                    className="logs-panel__reload"
                                    onClick={submitManualCard}
                                    disabled={isResolvingChoice}
                                >
                                    {isResolvingChoice ? 'Сохранение...' : 'Сохранить карточку'}
                                </button>
                                <button
                                    type="button"
                                    className="logs-panel__reload"
                                    onClick={cancelRun}
                                    disabled={isResolvingChoice}
                                >
                                    Отмена
                                </button>
                            </div>
                        </div>
                    )}
                    {isSolrStepDone && productId && (
                        <div className="autobase-actions" style={{ marginTop: '10px' }}>
                            <div className="autobase-actions__title" style={{ marginBottom: '6px' }}>
                                Действия с товаром
                            </div>
                            {deleteError && (
                                <div className="autobase-actions__error" style={{ marginBottom: '6px', color: '#b94a48' }}>
                                    {deleteError}
                                </div>
                            )}
                            {deleteSuccess && (
                                <div className="autobase-actions__success" style={{ marginBottom: '6px', color: '#3c763d' }}>
                                    {deleteSuccess}
                                </div>
                            )}
                            <div className="autobase-actions__buttons">
                                <button
                                    type="button"
                                    className="logs-panel__reload autobase-actions__button"
                                    onClick={handleDeleteProduct}
                                    disabled={isDeleting}
                                    style={{ marginRight: '8px' }}
                                >
                                    {isDeleting ? 'Удаление...' : 'Удалить товар'}
                                </button>
                                <button
                                    type="button"
                                    className="logs-panel__reload autobase-actions__button"
                                    onClick={handleOpenProductCard}
                                    style={{ marginRight: '8px' }}
                                >
                                    Открыть карточку товара
                                </button>
                                <button
                                    type="button"
                                    className="logs-panel__reload autobase-actions__button"
                                    onClick={() => {
                                        if (typeof onAddToOrder === 'function') {
                                            onAddToOrder({
                                                productId,
                                                partNumber: (partNumber || '').trim(),
                                            });
                                        }
                                    }}
                                >
                                    Добавить в заказ
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="logs-panel__body">
                {!error && isLoading && <div className="logs-panel__loading">Загрузка...</div>}
                {!error && (
                    <pre className="logs-viewer">
                        {result ? JSON.stringify(result, null, 2) : 'Результат будет показан здесь'}
                    </pre>
                )}
            </div>
        </div>
    );
};

export default AutobaseRunPanel;
